import { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import type { ProjectTask } from '@/types/odoo';
import type { ArchivedTask } from '@/api/planning';

/**
 * "Nog in te plannen" tray for the dashboard Inplannen scheduler — lists the active
 * fase's not-yet-dated tasks as @dnd-kit draggables, a "+ Nieuwe taak" chip that
 * creates a fresh task under the fase, and a collapsible "Verwijderde taken"
 * (archived) list whose items can be dragged back onto a day to un-archive them.
 * Rendered INSIDE PlanningGrid's DnD context (via its `renderAside` slot) so its
 * items drop onto grid cells.
 */
interface InplannenTrayProps {
  faseId: number | null;
  faseName: string;
  /** Active-fase, non-collector, undated tasks (already minus optimistically-placed). */
  unplannedTasks: ProjectTask[];
  /** Active-fase archived tasks — drag onto a day to recover (un-archive). */
  archivedTasks: ArchivedTask[];
}

function TrayDraggable({
  id,
  data,
  className,
  children,
}: {
  id: string;
  data: Record<string, unknown>;
  className?: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id, data });
  const style: React.CSSProperties | undefined = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50, position: 'relative' }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`cursor-grab touch-none select-none active:cursor-grabbing ${isDragging ? 'opacity-60 shadow-lg' : ''} ${className ?? ''}`}
    >
      {children}
    </div>
  );
}

export function InplannenTray({ faseId, faseName, unplannedTasks, archivedTasks }: InplannenTrayProps) {
  const [showArchived, setShowArchived] = useState(false);

  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-gray-50 p-3">
      <h3 className="text-sm font-extrabold text-td-navy">Nog in te plannen</h3>
      <p className="truncate text-xs text-td-muted">{faseName}</p>

      {faseId !== null && (
        <div className="mb-3 mt-2 border-b border-gray-200 pb-3">
          <TrayDraggable
            id={`tray-fase:${faseId}`}
            data={{ kind: 'fase', faseId }}
            className="flex items-center justify-center gap-1 rounded border border-dashed border-td-blue bg-td-blue/5 p-2 text-xs font-semibold text-td-blue hover:bg-td-blue/10"
          >
            + Nieuwe taak
          </TrayDraggable>
          <p className="mt-1 text-[10px] leading-tight text-td-muted">
            Sleep naar een dag om een nieuwe taak voor deze fase in te plannen (voegt de installateur toe als er die
            dag al een taak van deze fase staat).
          </p>
        </div>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto">
        {unplannedTasks.length === 0 ? (
          <p className="py-6 text-center text-xs text-td-muted">Alles in deze fase is ingepland.</p>
        ) : (
          unplannedTasks.map((t) => (
            <TrayDraggable
              key={t.id}
              id={`tray-task:${t.id}`}
              data={{ kind: 'unplanned-task', taskId: t.id }}
              className="rounded border-l-[3px] border-amber-500 bg-white p-2 shadow-sm hover:shadow"
            >
              <span className="block truncate text-xs font-bold text-td-navy">{t.name}</span>
              {t.x_studio_verdieping && (
                <span className="block truncate text-[10px] text-td-muted">{t.x_studio_verdieping}</span>
              )}
            </TrayDraggable>
          ))
        )}
      </div>

      {archivedTasks.length > 0 && (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="flex w-full items-center justify-between text-xs font-bold text-td-muted hover:text-td-navy"
          >
            <span>Verwijderde taken ({archivedTasks.length})</span>
            <svg
              className={`h-3.5 w-3.5 transition-transform ${showArchived ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showArchived && (
            <div className="mt-2 space-y-2">
              <p className="text-[10px] leading-tight text-td-muted">Sleep een verwijderde taak naar een dag om hem terug te zetten.</p>
              {archivedTasks.map((t) => (
                <TrayDraggable
                  key={t.id}
                  id={`tray-archived:${t.id}`}
                  data={{ kind: 'archived-task', taskId: t.id }}
                  className="rounded border-l-[3px] border-gray-400 bg-white/70 p-2 shadow-sm hover:shadow"
                >
                  <span className="block truncate text-xs font-semibold text-td-muted">{t.name}</span>
                </TrayDraggable>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
